/*
 * @Author: ningbo.kang
 * @Date: 2019-12-20 18:04:49
 * @LastEditors: wenpan.cao@ecarxgroup.com
 * @LastEditTime: 2022-12-02 12:09:13
 * @Description: 表单基础公共属性和方法，会被 mixins 适应
 */

import widgetOptions from './widgetOptions';
import { t } from '../locale';
import { getLangValue } from './index';

const basicComponents = [
  'blank',
  'cascader',
  'fileupload',
  'input',
  'number',
  'slider',
  'slider',
  'switch',
  'checkbox',
  'select',
  'rate',
  'date',
  'dateRange',
  'time',
  'treeSelect',
  'textarea',
  'radio',
  'link',
  'dialog',
  'table',
  // 将hidden注册到formContext.formItems
  'hidden',
];
const FORMAT_MAP = {
  'YYYY-MM-DD': 'MMM DD, YYYY',
  'YYYY-MM': 'MMM YYYY',
  YYYY: 'YYYY',
  'YYYY-MM-DD hh:mm:ss': 'MMM DD, YYYY hh:mm:ss',
};

export default {
  props: {
    value: {},
    // 是否嵌套在子表
    isNested: {
      type: Boolean,
      default: false,
    },
    // 嵌套时，父节点字段名,不包括[]
    parentName: {
      type: String,
      default: '',
    },
    // 嵌套时，父节点字段名
    parentModel: {
      type: String,
      default: '',
    },
    // 组件的嵌套的子组件数组，组件自己是容器
    list: {
      type: Array,
      default() {
        return [];
      },
    },
    // 组件自己属性，包含组件完全配置属性
    widget: {
      type: Object,
      default() {
        return {};
      },
    },

    // 组件是字段名
    fieldName: {
      type: String,
      default: '',
    },
    // 子表格行索引
    rowIndex: {
      type: Number,
      default: -1,
    },
    // 是否只读，等效于禁用
    readOnly: {
      type: Boolean,
      default: false,
    },
    // 是否禁用\可编辑
    disabled: {
      type: Boolean,
      default: true,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    // 子表布局
    layout: {
      type: String,
      default: 'table',
    },
    lang: {
      type: String,
      default: process.env.VUE_APP_DEFAULT_LANG,
    },
    placeholder: {
      type: String,
      default: '',
    },
    rangePlaceholder: {
      type: Array,
      default: () => [],
    },
  },
  inject: ['formContext'],
  data() {
    return {
      // 组价默认options
      options: {},
      // 默认规则
      otherRules: {},
      text: undefined,
      componentName: undefined,
      placeholderStyle: 'color: #aaa;font-size:26rpx',
      FORMAT_MAP,
    };
  },
  watch: {
    // 不能删除，应用在子表格，select 更新
    value: {
      handler(val, pre) {
        this.initVal(val, pre);
        // this.afterVal(val, pre);
      },
      immediate: true,
    },
    // 删除时，序号会断号，需要重新将当前组件更新到form
    rowIndex() {
      this.registerItemToForm();
    },
    widget: {
      deep: true,
      handler() {
        this.mergerOptions();
      },
    },
    // 将disabled属性传递到父元素
    disabled(val) {
      this.$emit('onDisabled', val);
    },
  },
  computed: {
    size() {
      if (this.isNested && !this.showLabel) {
        return 'small';
      }
      return 'default';
    },
    // disabled() {
    //   return this.config.disabled || this.options.disabled;
    // },
    // 从store中读取form配置
    config() {
      if (this.formContext) {
        return this.formContext.jsonData.config;
      }
      return {};
    },
    // 表单项标签名
    label() {
      // return this.showLabel ? this.widget.name : '';
      if (this.showLabel) {
        // 国际化需要 不再获取langTitle进行匹配，直接获取i18nTitle
        if (this.widget.i18nTitle) {
          return this.widget.i18nTitle;
        }
        if (this.widget.langTitle) {
          return getLangValue(this.widget.langTitle, this.lang);
        }
        return this.widget.name;
      }
      return '';
    },
    // text组件的 value
    textValue() {
      // 注释start
      if (this.widget.i18nPlaceholder) {
        const value = this.widget.i18nPlaceholder;
        if (value) {
          return `${value}`.replace(/\n/g, '<br/>');
        }
      }
      if (this.widget.langPlaceholder) {
        const value = getLangValue(this.widget.langPlaceholder, this.lang);
        if (value) {
          return `${value}`.replace(/\n/g, '<br/>');
        }
      }
      return `${this.widget.options.defaultValue}`.replace(/\n/g, '<br/>');
    },
    defaultValue() {
      const { options, type } = this.widget;
      // 配置默认值
      const configDefaultValue = widgetOptions[type].defaultValue;
      // if (type === 'select' && options.multiple) {
      //   configDefaultValue = widgetOptions[type].multiple.defaultValue;
      // }
      // 从 字段设置默认值 或 配置默认值 去除默认值
      return options.defaultValue || configDefaultValue;
    },
    remote() {
      if (this.formContext) {
        return this.formContext.remote;
      }
      return {};
    },
    // 动态
    dynamic() {
      if (this.formContext) {
        return this.formContext.dynamic;
      }
      return {};
    },
  },

  mounted() {
    this.registerItemToForm();
    this.mergerOptions();
  },
  destroyed() {
    // 如果有组件，卸载时从formItems 移除
    if (this.componentName) {
      if (this.formContext) {
        delete this.formContext.formItems[this.componentName];
      }
    }
  },
  methods: {
    t(...args) {
      return t.apply(this, args);
    },
    // 组件注册到form中
    registerItemToForm() {
      const { model, type } = this.widget;
      if (this.isBasic(type)) {
        // 将子组件需要暴露的方法注册到父wgForm中去
        let fieldName = model;
        if (this.isNested) {
          fieldName = `${this.parentModel}.${fieldName}`;
        }
        if (this.formContext) {
          this.formContext.formItems[fieldName] = this;
        }
        this.componentName = fieldName;
      }
    },
    isBasic(name) {
      return basicComponents.includes(name);
    },
    mergerOptions() {
      this.options = Object.assign({}, this.options, this.widget.options);
    },
    /**
     * 处理函数名和参数，分离出函数名和参数
     * @param {*} remoteFunc
     */
    separateFunc(remoteFunc) {
      const startIdx = remoteFunc.indexOf('(');
      if (startIdx > -1) {
        return {
          func: remoteFunc.substr(0, startIdx),
          args: remoteFunc
            .substr(startIdx + 1, remoteFunc.length - startIdx - 2)
            .split(','),
        };
      }
      return {
        func: remoteFunc,
        args: [],
      };
    },
    /**
     * 向上触发触发 componentName 组件的 eventName 事件
     * @param {*} componentName
     * @param {*} eventName
     * @param {*} params
     */
    dispatch(componentName, eventName, params = {}) {
      if (this.formContext) {
        this.formContext.$bus.emit('updateLink');
        this.formContext.$emit(eventName, params);
        const { isRelatedCompute = false } = (params && params.item && params.item.options) || {};
        if (isRelatedCompute) { // 当前响应内容为关联计算时
          this.formContext.$bus.emit('computeSetFn');
        }
      }
    },
    // 设置禁用
    setReadOnly(val) {
      this.$emit('noticeParent', { disabled: val });
    },
    // 设置必填
    setRequired(val) {
      this.$emit('noticeParent', { required: val });
    },
    // 增加数据校验器规则，为了删除和更新，给规则设置 ruleId
    addRule(ruleId, rule) {
      this.$emit('noticeParent', { rules: { ruleId, rule, opt: 'add' } });
    },
    // 删除数据校验器规则
    removeRule(ruleId) {
      this.$emit('noticeParent', { rules: { ruleId, opt: 'remove' } });
    },
    // 修正value值
    initVal(val) {
      // 不使用默认值，使用用户设置的，用户会设置null,undefined，不改变用户意图
      this.text = val;
    },
    // 初始化完成后
    afterVal(val) {
      // 存在 model_hidden命名规则的，则个其赋值
      if (
        [
          'cascader',
          'fileupload',
          'input',
          'number',
          'textarea',
          'slider',
          'switch',
          'radio',
          'checkbox',
          'select',
          'rate',
          'treeSelect',
          'dialog',
        ].includes(this.widget.type)
      ) {
        let key = `${this.widget.model}_hidden`;
        if (this.isNested) {
          key = `${this.parentModel}.${this.widget.model}_hidden`;
        }
        if (this.formContext) {
          if (this.formContext.formItems[key]) {
            this.formContext.form.setFieldsValue({
              [key]: val,
            });
          }
        }
      }
    },
    // 将字符串转换为数字
    valueToNumber(val) {
      // 无意义空值
      if (val === null || val === undefined || val === '') {
        return null;
      }
      // 如果是字符串
      if (Number.isNaN(Number(val))) {
        return null;
      }
      return Number(val);
    },
    // 修改format格式
    transFormat(format) {
      const lang = this.$root.$i18n.locale || this.lang;
      return (lang === 'zh_CN' || lang === 'zh-CN') ? format : FORMAT_MAP[format];
    },
  },
};
